import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';

// ── Sprint E: fully API-driven Homepage Sections ─────────────────────────────

const GRAND_FINALE = new Date('2026-12-30T00:00:00+01:00');

// Static village list with icons (heritage shown on CommunitiesShowcase)
const VILLAGES = [
  { id:'v01', name:'Jattu-Uzairue',      icon:'👑' },
  { id:'v02', name:'Afashio-Uzairue',    icon:'🛡️' },
  { id:'v03', name:'Afowa-Uzairue',      icon:'🌳' },
  { id:'v04', name:'Apana-Uzairue',      icon:'🌊' },
  { id:'v05', name:'Ayaoghena-Uzairue',  icon:'🎭' },
  { id:'v06', name:'Ayogwiri-Uzairue',   icon:'⚔️' },
  { id:'v07', name:'Ayua-Uzairue',       icon:'🌿' },
  { id:'v08', name:'Elele-Uzairue',      icon:'🌾' },
  { id:'v09', name:'Idatto-Uzairue',     icon:'🏺' },
  { id:'v10', name:'Ikabigbo-Uzairue',   icon:'🥊' },
  { id:'v11', name:'Ikholo-Uzairue',     icon:'⚖️' },
  { id:'v12', name:'Imeke-Uzairue',      icon:'🧵' },
  { id:'v13', name:'Imhonikhe-Uzairue',  icon:'🏛️' },
  { id:'v14', name:'Irekpai-Uzairue',    icon:'⚒️' },
  { id:'v15', name:'Iyamho-Uzairue',     icon:'📚' },
  { id:'v16', name:'Iyora-Uzairue',      icon:'🎪' },
  { id:'v17', name:'Iyuku-Uzairue',      icon:'🏪' },
  { id:'v18', name:'Ogbido-Uzairue',     icon:'🌿' },
  { id:'v19', name:'Ozor-Uzairue',       icon:'🌲' },
  { id:'v20', name:'Ugbenor-Uzairue',    icon:'🌴' },
  { id:'v21', name:'Uluoke-Uzairue',     icon:'⛰️' },
  { id:'v22', name:'Yelwa-Uzairue',      icon:'💰' },
];

const SPECIAL = [
  { id:'sp01', name:'Igala Community',    icon:'🤝' },
  { id:'sp02', name:'Igbo Community',     icon:'🌟' },
  { id:'sp03', name:'Yoruba Community',   icon:'🎺' },
  { id:'sp04', name:'Diaspora Community', icon:'✈️' },
];

// Day-of-week fallback icons keyed by dayLabel keyword
const DAY_ICONS: Record<string, string> = {
  opening:    '🎺',
  cultural:   '🥁',
  parade:     '🥁',
  sport:      '⚽',
  community:  '🤝',
  heritage:   '🏛️',
  dignitar:   '👑',
  finale:     '🎆',
  gala:       '🌟',
  night:      '🌙',
};

function dayIcon(label: string): string {
  const l = label.toLowerCase();
  for (const [k, v] of Object.entries(DAY_ICONS)) {
    if (l.includes(k)) return v;
  }
  return '📅';
}

interface CountdownValue { days: number; hours: number; minutes: number; seconds: number; }

function useCountdown(target: Date): CountdownValue {
  const calc = (): CountdownValue => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days:    Math.floor(diff / 86400000),
      hours:   Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000)  / 60000),
      seconds: Math.floor((diff % 60000)    / 1000),
    };
  };
  const [val, setVal] = useState<CountdownValue>(calc);
  useEffect(() => {
    const id = setInterval(() => setVal(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return val;
}

function CountdownBox({ value, label }: { value: number; label: string }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 72 }}>
      <div style={{
        background: 'rgba(201,168,76,0.10)',
        border: '1px solid rgba(201,168,76,0.35)',
        borderRadius: 12,
        padding: '16px 12px 10px',
        marginBottom: 6,
      }}>
        <span style={{
          fontFamily: 'Cinzel, serif',
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: 700,
          color: '#C9A84C',
          lineHeight: 1,
          display: 'block',
        }}>
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span style={{ fontSize: 11, letterSpacing: 2, color: '#EDE8DC99', textTransform: 'uppercase' as const }}>
        {label}
      </span>
    </div>
  );
}

interface ProgrammeDay {
  id: string;
  date: string;
  dayLabel: string;
  theme: string;
  events: any[];
}

interface Highlight {
  id: string;
  title: string;
  description?: string;
  type?: string;
  isHighlight?: boolean;
  programmeDay?: { dayLabel: string; date: string; theme?: string };
}

interface HomepageSectionsProps {
  onNavigate?: (view: string) => void;
}

const HomepageSections: React.FC<HomepageSectionsProps> = ({ onNavigate }) => {
  const cd = useCountdown(GRAND_FINALE);

  // ── Programme from API ──────────────────────────────────────────────────────
  const [programmeDays, setProgrammeDays] = useState<ProgrammeDay[]>([]);
  const [progLoading, setProgLoading]     = useState(true);

  useEffect(() => {
    fetch('/api/programme')
      .then(r => r.json())
      .then(d => {
        const raw: any[] = Array.isArray(d) ? d
          : Array.isArray(d.days)      ? d.days
          : Array.isArray(d.programme) ? d.programme
          : [];
        setProgrammeDays(raw.map(day => ({
          id:       day.id || day.dayId || '',
          date:     day.date || '',
          dayLabel: day.dayLabel || day.label || '',
          theme:    day.theme || '',
          events:   Array.isArray(day.events) ? day.events : [],
        })));
      })
      .catch(() => setProgrammeDays([]))
      .finally(() => setProgLoading(false));
  }, []);

  // ── Highlights from API ─────────────────────────────────────────────────────
  const [highlights, setHighlights]    = useState<Highlight[]>([]);
  const [hlLoading, setHlLoading]      = useState(true);

  useEffect(() => {
    fetch('/api/public/highlights')
      .then(r => r.json())
      .then(d => {
        const arr = Array.isArray(d) ? d : (d.highlights || []);
        setHighlights(arr);
      })
      .catch(() => setHighlights([]))
      .finally(() => setHlLoading(false));
  }, []);

  const totalEvents = programmeDays.reduce((s, d) => s + (d.events?.length || 0), 0);
  const hasRealProgramme = !progLoading && programmeDays.length > 0 && totalEvents > 0;

  return (
    <div style={{ background: '#070D0A', color: '#EDE8DC' }}>

      {/* ① COUNTDOWN ─────────────────────────────────────────── */}
      <section style={{
        background: 'linear-gradient(180deg, #070D0A 0%, #0A1A0F 100%)',
        borderBottom: '1px solid #1C2E22',
        padding: 'clamp(48px, 8vw, 80px) 24px',
        textAlign: 'center',
      }}>
        <p style={{ color: '#C9A84C', fontSize: 12, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Grand Finale Countdown
        </p>
        <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.3rem, 3vw, 2rem)', color: '#EDE8DC', marginBottom: 8, fontWeight: 600 }}>
          Wednesday, 30 December 2026
        </h2>
        <p style={{ color: '#EDE8DC80', fontSize: 13, marginBottom: 36, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Palace of the Ogieneni of Uzairue Kingdom, Jattu-Uzairue
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 'clamp(12px, 3vw, 28px)', flexWrap: 'wrap' }}>
          <CountdownBox value={cd.days}    label="Days" />
          <span style={{ color: '#C9A84C', fontSize: '2.5rem', marginTop: 14, fontWeight: 300 }}>:</span>
          <CountdownBox value={cd.hours}   label="Hours" />
          <span style={{ color: '#C9A84C', fontSize: '2.5rem', marginTop: 14, fontWeight: 300 }}>:</span>
          <CountdownBox value={cd.minutes} label="Minutes" />
          <span style={{ color: '#C9A84C', fontSize: '2.5rem', marginTop: 14, fontWeight: 300 }}>:</span>
          <CountdownBox value={cd.seconds} label="Seconds" />
        </div>
      </section>

      {/* ② ABOUT UZAIRUE KINGDOM ──────────────────────────────── */}
      <section style={{
        maxWidth: 1100, margin: '0 auto',
        padding: 'clamp(48px, 8vw, 88px) 24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 48, alignItems: 'center',
      }}>
        <div>
          <p style={{ color: '#C9A84C', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 14, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            About the Kingdom
          </p>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: '#EDE8DC', marginBottom: 20, lineHeight: 1.3 }}>
            Uzairue Kingdom
          </h2>
          <p style={{ color: '#EDE8DCCC', lineHeight: 1.8, marginBottom: 16, fontSize: 15, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Uzairue Kingdom is a proud Etsako community in Etsako West Local Government Area, Edo State, Nigeria.
            The Kingdom comprises <strong style={{ color: '#C9A84C' }}>22 villages and component communities</strong>, united
            under the sovereign leadership of HRH the Ogieneni of Uzairue Kingdom.
          </p>
          <p style={{ color: '#EDE8DCCC', lineHeight: 1.8, marginBottom: 28, fontSize: 15, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            The annual ERUE Festival is a celebration of heritage, unity and shared destiny —
            bringing home sons and daughters from across Nigeria and the diaspora every December.
          </p>
          <div style={{ display: 'inline-block', padding: '10px 24px', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, fontFamily: 'Cinzel, serif', fontSize: 13, color: '#C9A84C', letterSpacing: 1 }}>
            "One People, One Kingdom, One Destiny"
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ borderRadius: 16, overflow: 'hidden', border: '2px solid rgba(201,168,76,0.4)', aspectRatio: '3/4', maxHeight: 420, width: '100%', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
            <img
              src="https://res.cloudinary.com/uuovmrek/image/upload/ogienenimai002"
              alt="HRH The Ogieneni of Uzairue Kingdom"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
          <p style={{ textAlign: 'center', fontSize: 12, color: '#C9A84C99', fontFamily: 'Plus Jakarta Sans, sans-serif', marginTop: 2 }}>
            HRH The Ogieneni of Uzairue Kingdom · Royal Patron of ERUE Festival 2026
          </p>
        </div>
      </section>

      {/* ③ PROGRAMME OF EVENTS — API-driven ──────────────────── */}
      <section style={{ background: '#0E1812', borderTop: '1px solid #1C2E22', borderBottom: '1px solid #1C2E22', padding: 'clamp(48px, 8vw, 80px) 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ color: '#C9A84C', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 10, textAlign: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Festival Calendar
          </p>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: '#EDE8DC', marginBottom: 8, textAlign: 'center' }}>
            Programme of Events
          </h2>
          <p style={{ color: '#EDE8DC80', fontSize: 13, textAlign: 'center', marginBottom: 40, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            December 24 – 30, 2026 · Uzairue Kingdom, Etsako West LGA, Edo State
          </p>

          {progLoading && (
            <p style={{ textAlign: 'center', color: 'rgba(237,232,220,0.3)', fontSize: 14 }}>Loading programme...</p>
          )}

          {!progLoading && hasRealProgramme && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
              {programmeDays.map(day => {
                const evtCount   = day.events?.length || 0;
                const isFinale   = day.dayLabel?.toLowerCase().includes('finale') || day.dayLabel?.toLowerCase().includes('grand');
                const icon       = dayIcon(day.dayLabel || '');
                const dateLabel  = day.date
                  ? new Date(day.date + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' })
                  : day.dayLabel;
                return (
                  <div key={day.id} style={{
                    background: isFinale ? 'linear-gradient(135deg, rgba(201,168,76,0.15), rgba(15,81,50,0.25))' : '#121E16',
                    border: isFinale ? '1px solid rgba(201,168,76,0.5)' : '1px solid #1C2E22',
                    borderRadius: 12, padding: '20px 18px', position: 'relative', cursor: 'pointer',
                    transition: 'transform 0.2s, border-color 0.2s',
                  }}
                    onClick={() => onNavigate && onNavigate('programme')}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.4)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.borderColor = isFinale ? 'rgba(201,168,76,0.5)' : '#1C2E22'; }}
                  >
                    {isFinale && (
                      <span style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, letterSpacing: 2, color: '#C9A84C', textTransform: 'uppercase', fontFamily: 'Plus Jakarta Sans, sans-serif', background: 'rgba(201,168,76,0.15)', padding: '2px 8px', borderRadius: 20 }}>
                        Grand Finale
                      </span>
                    )}
                    <div style={{ fontSize: 28, marginBottom: 10 }}>{icon}</div>
                    <p style={{ fontSize: 11, color: '#C9A84C', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {dateLabel}
                    </p>
                    <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 15, color: '#EDE8DC', marginBottom: 8, fontWeight: 600 }}>
                      {day.dayLabel}
                    </h3>
                    <p style={{ fontSize: 13, color: '#EDE8DC99', lineHeight: 1.6, fontFamily: 'Plus Jakarta Sans, sans-serif', margin: 0 }}>
                      {day.theme || `${evtCount} event${evtCount !== 1 ? 's' : ''} scheduled`}
                    </p>
                    <p style={{ fontSize: 11, color: 'rgba(201,168,76,0.6)', marginTop: 8, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {evtCount} event{evtCount !== 1 ? 's' : ''} · Click for full programme →
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {!progLoading && !hasRealProgramme && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <p style={{ color: 'rgba(237,232,220,0.4)', fontSize: 14, marginBottom: 8 }}>
                The full programme for ERUE Festival 2026 (December 24–30) is being finalised by the LOC.
              </p>
              <p style={{ color: 'rgba(201,168,76,0.5)', fontSize: 13, fontStyle: 'italic' }}>
                "Uzairue Heritage: Culture, Security, Prosperity"
              </p>
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button
              onClick={() => onNavigate && onNavigate('programme')}
              style={{ padding: '12px 32px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 10, color: '#C9A84C', fontSize: 13, fontFamily: 'Cinzel, serif', fontWeight: 600, cursor: 'pointer', letterSpacing: 1, transition: 'background 0.2s' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.22)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.12)'}
            >
              📅 View Full Festival Programme →
            </button>
          </div>
        </div>
      </section>

      {/* ④ FESTIVAL HIGHLIGHTS — API-driven ──────────────────── */}
      {!hlLoading && highlights.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(48px, 8vw, 80px) 24px' }}>
          <p style={{ color: '#C9A84C', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 10, textAlign: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Not to be Missed
          </p>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: '#EDE8DC', marginBottom: 8, textAlign: 'center' }}>
            Festival Highlights
          </h2>
          <p style={{ color: '#EDE8DC80', fontSize: 13, textAlign: 'center', marginBottom: 40, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Key events and moments from ERUE Festival 2026
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {highlights.slice(0, 6).map(h => (
              <div key={h.id} style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(15,81,50,0.15))', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 12, padding: '20px 18px', cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={() => onNavigate && onNavigate('programme')}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Star size={14} fill="#C9A84C" style={{ color: '#C9A84C', flexShrink: 0 }} />
                  <span style={{ fontSize: 10, letterSpacing: 2, color: '#C9A84C', textTransform: 'uppercase', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {h.programmeDay?.dayLabel || h.type || 'Highlight'}
                  </span>
                </div>
                <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: 14, color: '#EDE8DC', marginBottom: 8, fontWeight: 600, lineHeight: 1.4 }}>
                  {h.title}
                </h3>
                {h.description && (
                  <p style={{ fontSize: 12, color: '#EDE8DC80', lineHeight: 1.6, margin: 0, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {h.description.length > 100 ? h.description.substring(0, 100) + '...' : h.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ⑤ COMMUNITIES GRID — clickable, links to communities tab ───────────── */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(48px, 8vw, 80px) 24px' }}>
        <p style={{ color: '#C9A84C', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 10, textAlign: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          The Kingdom
        </p>
        <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: '#EDE8DC', marginBottom: 8, textAlign: 'center' }}>
          22 Villages and Component Communities
        </h2>
        <p style={{ color: '#EDE8DC80', fontSize: 13, textAlign: 'center', marginBottom: 40, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          United under the Ogieneni of Uzairue Kingdom — click any community to explore its portal
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 32 }}>
          {VILLAGES.map(v => (
            <div key={v.id}
              onClick={() => onNavigate && onNavigate('communities')}
              style={{ background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 10, padding: '14px 12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s' }}
              onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = 'rgba(201,168,76,0.4)'; el.style.background = '#0F1F14'; }}
              onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = '#1C2E22'; el.style.background = '#0E1812'; }}
              title={`Open ${v.name} portal`}
            >
              <span style={{ fontSize: 20, flexShrink: 0 }}>{v.icon}</span>
              <span style={{ fontSize: 12, color: '#EDE8DCCC', fontFamily: 'Plus Jakarta Sans, sans-serif', lineHeight: 1.3 }}>
                {v.name}
              </span>
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid #1C2E22', paddingTop: 24, marginTop: 8 }}>
          <p style={{ color: '#EDE8DC80', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14, textAlign: 'center', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Plus our valued component communities
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {SPECIAL.map(s => (
              <div key={s.id}
                onClick={() => onNavigate && onNavigate('communities')}
                style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s' }}
                onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = 'rgba(201,168,76,0.5)'; el.style.background = 'rgba(201,168,76,0.10)'; }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = 'rgba(201,168,76,0.25)'; el.style.background = 'rgba(201,168,76,0.05)'; }}
                title={`Open ${s.name} portal`}
              >
                <span style={{ fontSize: 22, flexShrink: 0 }}>{s.icon}</span>
                <span style={{ fontSize: 14, color: '#E2C97E', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 500 }}>
                  {s.name}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 36 }}>
          <button
            onClick={() => onNavigate && onNavigate('communities')}
            style={{ padding: '12px 32px', background: 'rgba(96,165,250,0.10)', border: '1px solid rgba(96,165,250,0.35)', borderRadius: 10, color: '#60A5FA', fontSize: 13, fontFamily: 'Cinzel, serif', fontWeight: 600, cursor: 'pointer', letterSpacing: 1, transition: 'background 0.2s' }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(96,165,250,0.18)'}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'rgba(96,165,250,0.10)'}
          >
            🏘️ Explore All 26 Communities →
          </button>
        </div>
      </section>

      {/* ⑥ SPONSORS / PARTNERS ────────────────────────────────── */}
      <section style={{ background: '#0E1812', borderTop: '1px solid #1C2E22', borderBottom: '1px solid #1C2E22', padding: 'clamp(40px, 6vw, 64px) 24px', textAlign: 'center' }}>
        <p style={{ color: '#C9A84C', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Partners &amp; Supporters
        </p>
        <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)', color: '#EDE8DC', marginBottom: 16 }}>
          Become a Festival Sponsor
        </h2>
        <p style={{ color: '#EDE8DC80', fontSize: 14, maxWidth: 520, margin: '0 auto 28px', lineHeight: 1.7, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Partner with the ERUE Festival of Uzairue Kingdom and connect your brand with thousands of
          attendees across Nigeria and the diaspora.
        </p>
        <div style={{ display: 'inline-flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <a href="https://wa.me/2347057840320" target="_blank" rel="noopener noreferrer" style={{ padding: '10px 28px', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 8, color: '#C9A84C', fontSize: 13, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, textDecoration: 'none', letterSpacing: 0.5 }}>
            💬 Enquire via WhatsApp
          </a>
          <button
            onClick={() => onNavigate && onNavigate('sponsorship')}
            style={{ padding: '10px 28px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 8, color: '#C9A84C', fontSize: 13, fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5 }}
          >
            🤝 View Sponsorship Packages
          </button>
        </div>
      </section>

    </div>
  );
};

export default HomepageSections;
